import { ReadAllResponse, uuid } from "@/interfaces";
import { store } from "@/lib/store.ts";
import { requirePermission } from "@/lib/permissions";
import { api } from "@/lib/api";
import { expiresOnOf } from "@/lib/documents";
import { DocumentsRepository } from "@/repositories/documents.repository";
import { ApiRequestError, SUBCONTRACT_PAGE_SIZE_MAX, type DocumentOwner, type DocumentRecord, type ReachedSubcontractor, type Subcontract, type SubcontractContactRequest, type SubcontractListQuery } from "@shyne/shared/api";

export type SubcontractSortKey = SubcontractListQuery["sort"];

export type SubcontractStatus = SubcontractListQuery["status"];

export type SubsInviteOutcome = { alreadyOnboarded: boolean };

function activeOrganization(): uuid {
  const { activeOrgId } = store.getState().organization;
  if (!activeOrgId) throw new Error("No active organization");
  return activeOrgId;
}

const subcontractDocuments = (subcontractId: uuid): DocumentOwner => ({ kind: "subcontract", subcontract: subcontractId });

const isUnreachedSubcontractor = (error: unknown) => error instanceof ApiRequestError && error.code === "no_such_subcontractor";

export const SubsRepository = {
  async listByCurrentOrg(
    page = 1,
    pageSize = 10,
    name?: string,
    service?: string,
    sort: SubcontractSortKey = "name",
    order: "asc" | "desc" = "asc",
    status: SubcontractStatus = "live"
  ): Promise<ReadAllResponse<Subcontract>> {
    const { items, total } = await api.subcontracts.list({ actingOrganization: activeOrganization(), q: name, service, status, sort, order, page, pageSize });
    return { data: items, count: total };
  },

  async listAllByCurrentOrg(): Promise<Subcontract[]> {
    const actingOrganization = activeOrganization();
    const subcontracts: Subcontract[] = [];
    for (let page = 1; ; page++) {
      const { items, total } = await api.subcontracts.list({ actingOrganization, page, pageSize: SUBCONTRACT_PAGE_SIZE_MAX });
      subcontracts.push(...items);
      if (items.length < SUBCONTRACT_PAGE_SIZE_MAX || subcontracts.length >= total) return subcontracts;
    }
  },

  async countByCurrentOrg(): Promise<number> {
    return (await api.subcontracts.list({ actingOrganization: activeOrganization(), pageSize: 1 })).total;
  },

  async getDetail(subcontractId: uuid): Promise<Subcontract> {
    return api.subcontracts.get(subcontractId, { actingOrganization: activeOrganization() });
  },

  async searchSubcontractors(query: string): Promise<ReachedSubcontractor[]> {
    return (await api.subcontractors.search({ actingOrganization: activeOrganization(), q: query })).items;
  },

  async uploadDocument(subcontractId: uuid, file: File, expiresAt?: string | Date): Promise<DocumentRecord> {
    return DocumentsRepository.upload(subcontractDocuments(subcontractId), file, expiresOnOf(expiresAt));
  },

  async deleteDocument(docId: uuid): Promise<void> {
    await DocumentsRepository.remove(docId);
  },

  async updateDocumentExpiresAt(docId: uuid, expiresAt: string | Date | null): Promise<DocumentRecord> {
    return DocumentsRepository.setExpiry(docId, expiresOnOf(expiresAt));
  },

  async listDocuments(subcontractId: uuid, page = 1, pageSize = 10): Promise<ReadAllResponse<DocumentRecord>> {
    return DocumentsRepository.page(subcontractDocuments(subcontractId), page, pageSize);
  },

  async setServices(subcontractId: uuid, services: uuid[]): Promise<Subcontract> {
    return api.subcontracts.setServices(subcontractId, { services });
  },

  async setContact(subcontractId: uuid, contact: SubcontractContactRequest): Promise<Subcontract> {
    return api.subcontracts.setContact(subcontractId, contact);
  },

  async remove(subcontractId: uuid): Promise<void> {
    await api.subcontracts.end(subcontractId);
  },

  async addExistingSubs(subcontractor: uuid): Promise<Subcontract> {
    return api.subcontracts.link({ actingOrganization: activeOrganization(), subcontractor });
  },

  async inviteSubs(email: string, services: uuid[] = []): Promise<SubsInviteOutcome> {
    await requirePermission("subs.invite");

    const vendorOrgId = activeOrganization();

    try {
      await api.subcontracts.link({ actingOrganization: vendorOrgId, email });
      return { alreadyOnboarded: true };
    } catch (error) {
      if (!isUnreachedSubcontractor(error)) throw error;
    }

    await api.invites.send(vendorOrgId, { kind: "subcontractor", email, services });

    return { alreadyOnboarded: false };
  },
};
